import { useQueries } from '@tanstack/react-query'
import { Dayjs } from 'dayjs'

import { add, getDateIntervals, getToday } from '~/astra'
import env from '~/env'
import { importFullScheduledSurgeries } from '~/store/dips-entity.api'

import { combineQueries } from '../../shared/queryHelpers'
import { useSentryLogging } from '../../shared/useSentryLogging'
import { MultiQueryOptions } from '../useMultiQuery'

/**
 * IMPORTANT!
 * The date-range for importFullScheduledSurgeries is restricted to 7 days by default on DIPS' side. Exceeding this will return 403 Forbidden.
 * This can be overridden by the hospitals. Because we use monthly queries and the end date is exclusive, a minimum range of 32 days is required for Hero to run properly.
 * Hence, DAYS_PER_MONTH should not exceed 32.
 */
const DAYS_PER_MONTH = 30

export function getDefaultScheduledSurgeriesDateRange() {
    const startDate = getToday()
    const endDate = add(startDate, 179, 'days') // 6 intervals of 30 days (to stay within 6 queries)

    return {
        startDate,
        endDate,
    }
}

export type ScheduledSurgeriesArgs = {
    startDate?: Dayjs
    endDate?: Dayjs
    options?: MultiQueryOptions
}

export const SCHEDULED_SURGERIES_QUERYKEY_PREFIX = 'fullScheduledSurgeries'

/**
 * Fetch scheduled surgeries within a specified date range and put them in the store.
 *
 * IMPORTANT: if you need surgery metadata, use `useScheduledSurgeriesAndMetadataQueries` instead.
 *
 * @param startDate - (Optional) The start date (inclusive). Defaults to today.
 * @param endDate - (Optional) The end date (inclusive). Defaults to ~6 months from today.
 * @param options - (Optional) Parameters for the queries, such as skip and refetchInterval.
 */
export const useScheduledSurgeriesQueries = ({ startDate, endDate, options }: ScheduledSurgeriesArgs) => {
    const { startDate: defaultStartDate, endDate: defaultEndDate } = getDefaultScheduledSurgeriesDateRange()

    const start = startDate ?? defaultStartDate
    const end = endDate ?? defaultEndDate
    const dateIntervals = getDateIntervals({ start, end, intervalLength: DAYS_PER_MONTH })
    const treatmentLocationId = Number(env.VITE_DIPS_TREATMENT_LOCATION_ID)

    const futureScheduledSurgeriesQueries = useQueries({
        queries: dateIntervals.map(({ startTime, endTime }) => ({
            queryFn: () => importFullScheduledSurgeries(startTime, endTime, treatmentLocationId),
            queryKey: [SCHEDULED_SURGERIES_QUERYKEY_PREFIX, { startTime, endTime, treatmentLocationId }],
            enabled: !options?.skip,
            refetchInterval: options?.refetchInterval,
            staleTime: options?.staleTime,
        })),
        combine: combineQueries,
    })

    useSentryLogging({ key: 'useScheduledSurgeriesQueries', queryResult: futureScheduledSurgeriesQueries })

    return futureScheduledSurgeriesQueries
}
